import { Component, DestroyRef, OnInit } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { API, CampaignLevelDTO, CompanyDTO } from '../../../services/api/api';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { ErrorService } from '../../../services/error.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

@Component({
  selector: 'sb-comp-company-level-edit',
  templateUrl: './company-level-edit.component.html',
  styleUrls: ['./company-level-edit.component.scss'],
  standalone: false,
})
export class CompanyLevelEditComponent implements OnInit {
  form: UntypedFormGroup;
  isInModalContext: boolean;
  level: CampaignLevelDTO;
  levelOptions: CampaignLevelDTO[];
  company: CompanyDTO;
  formValid: boolean;
  initLevel: CampaignLevelDTO;

  constructor(
    private modalService: ModalNavigationService,
    private api: API,
    private errorHandler: ErrorService,
    private destroyRef: DestroyRef
  ) {
    this.levelOptions = [];
    this.form = new UntypedFormGroup(
      {
        Level: new FormControl('', [Validators.required]),
        Title: new UntypedFormControl('', [Validators.required]),
        Description: new UntypedFormControl('', []),
      },
      [this.isBetweenNumbers, this.isTitleUnique, this.isLevelUnique]
    );
    this.formValid = false;
    this.initLevel = null;
  }

  get Level() {
    return this.form.get('Level');
  }

  get Title() {
    return this.form.get('Title');
  }

  get Description() {
    return this.form.get('Description');
  }

  isBetweenNumbers: ValidatorFn = (fg: UntypedFormGroup) => {
    const levelNumber = fg.get('Level').value;
    if (!levelNumber) {
      return { error: true };
    }

    if (levelNumber >= 0 && levelNumber <= 100) {
      return null;
    }
    return { isBetweenNumbers: true };
  };

  isTitleUnique: ValidatorFn = (fg: UntypedFormGroup) => {
    const levelName = fg.get('Title').value;

    if (!levelName) {
      return { error: true };
    }

    if (!fg.get('Title') || this.levelOptions?.length <= 0) {
      fg.get('Title').setErrors(null);
      return null;
    }
    let err = false;
    for (const lvl of this.levelOptions) {
      if (this.initLevel) {
        if (levelName?.toLowerCase() === lvl.name?.toLowerCase() && levelName?.toLowerCase() !== this.initLevel.name?.toLowerCase()) {
          err = true;
          break;
        }
      } else if (levelName?.toLowerCase() === lvl.name?.toLowerCase()) {
        err = true;
        break;
      }
    }

    if (err) {
      fg.get('Title').setErrors({ error: true });
      return { isTitleUnique: true };
    } else {
      fg.get('Title').setErrors(null);
      return null; // title unique error  {isTitleUnique: 'false'};
    }
  };

  isLevelUnique: ValidatorFn = (fg: UntypedFormGroup) => {
    const levelNumber = fg.get('Level').value;

    if (!levelNumber) {
      return { error: true };
    }

    if (!fg.get('Level') || this.levelOptions?.length <= 0) {
      fg.get('Level').setErrors(null);
      return null;
    }
    let err = false;
    for (const lvl of this.levelOptions) {
      if (this.initLevel) {
        if (levelNumber === lvl.id.level && levelNumber !== this.initLevel.id.level) {
          err = true;
          break;
        }
      } else if (levelNumber === lvl.id.level) {
        err = true;
        break;
      }
    }
    if (err) {
      fg.get('Level').setErrors({ error: true });
      return { isLevelUnique: true };
    } else {
      fg.get('Level').setErrors(null);
      return null; // level unique error  {isLevelUnique: false};
    }
  };

  private registerFormChangesListener() {
    this.Title.valueChanges.subscribe(value => {
      this.level.name = value;
      this.checkFormValidity();
    });
    this.Level.valueChanges.subscribe(value => {
      this.level.id.level = value;
      this.checkFormValidity();
    });
    this.Description.valueChanges.subscribe(value => {
      this.level.description = value;
      this.checkFormValidity();
    });
  }

  updateForm() {
    this.Title.setValue(this.initLevel.name);
    this.Level.setValue(this.initLevel.id.level);
    this.Level.disable();
    if (this.initLevel.id.level === 0) {
      this.Title.disable();
    } else {
      this.Level.markAsTouched();
      this.Title.markAsTouched();
    }
    this.Description.setValue(this.initLevel.description);
    this.Description.markAsTouched();
    this.checkFormValidity();
  }

  ngOnInit(): void {
    if (this.isInModalContext) {
      // JSON parse and stringify needed for deep copy since levelOptions includes Object
      this.levelOptions = JSON.parse(JSON.stringify(this.modalService.getCurrentNavigation().data.levelOptions));
      this.level = JSON.parse(JSON.stringify(this.modalService.getCurrentNavigation().data.level));
      this.company = this.modalService.getCurrentNavigation().data.companyData;
      if (this.level.name && !this.level.id) {
        this.initLevel = JSON.parse(JSON.stringify(this.modalService.getCurrentNavigation().data.level));
        this.updateForm();
      } else if (this.level.name && this.level.id) {
        this.initLevel = this.level;
        this.updateForm();
      }
    }
    this.registerFormChangesListener();
  }

  sendLevel() {
    if (this.level?.id?.client) {
      this.api
        .ds_cm_Campaign_editLevel(this.company.id, this.level)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (data: CampaignLevelDTO) => {
            this.modalService.result(data);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.api
        .ds_cm_Campaign_createLevel(this.company.id, this.level)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (data: CampaignLevelDTO) => {
            this.modalService.result(data);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  checkFormValidity() {
    if (!this.Level.value) {
      this.formValid = false;
      return;
    }
    if (!this.Title.value) {
      this.formValid = false;
      return;
    }
    for (const lvl of this.levelOptions) {
      if (this.initLevel) {
        if (
          (this.level.name?.toLowerCase() === lvl.name?.toLowerCase() &&
            this.initLevel.name?.toLowerCase() !== this.level.name?.toLowerCase()) ||
          (this.level.id.level === lvl.id.level && this.initLevel.id.level !== this.level.id.level)
        ) {
          this.formValid = false;
          return;
        }
      } else if (this.level.name?.toLowerCase() === lvl.name?.toLowerCase() || this.level.id.level === lvl.id.level) {
        this.formValid = false;
        return;
      }
    }
    this.formValid = true;
  }
}
